import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { PropertyUnit } from "@/models/properties.model";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { uuid } from "@/interfaces";

const formSchema = z.object({
  unit_number: z.string().min(1, "Unit number is required"),
  floor_plan: z.string().optional(),
  beds: z.coerce.number().min(0, "Beds must be ≥ 0"),
  baths: z.coerce.number().min(0, "Baths must be ≥ 0"),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: Omit<PropertyUnit, "id" | "created_at" | "updated_at" | "deleted_at">) => void;
  propertyId: uuid;
}

export default function UnitCreationModal({ open, onOpenChange, onSubmit, propertyId }: Props) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      unit_number: "",
      floor_plan: "",
      beds: 0,
      baths: 0,
    },
    mode: "onSubmit",
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const handleSubmit = (values: FormValues) => {
    // Ensure all required fields are provided
    onSubmit({
      unit_number: values.unit_number, // Required - already validated by schema
      floor_plan: values.floor_plan || "",
      beds: values.beds,
      baths: values.baths,
      property: propertyId,
    });
    form.reset();
  };

  const fields: FormFieldDef[] = [
    {
      type: "text",
      name: "unit_number",
      label: "Unit Number",
      required: true,
    },
    {
      type: "text",
      name: "floor_plan",
      label: "Floor Plan",
      required: false,
    },
    {
      type: "number",
      name: "beds",
      label: "Beds",
      required: true,
    },
    {
      type: "number",
      name: "baths",
      label: "Baths",
      required: true,
    },
  ];

  const formCreatorProps: FormCreatorProps = {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    formDefinition: fields,
    buttonText: "Create Unit",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add New Unit</SheetTitle>
          <SheetDescription>
            <FormCreator {...formCreatorProps} />
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}

